import { ArrowRight, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { isProduction } from '@/config/config'

const DOT_REGEX = /^\d{1,8}$/
// Cai's own DOT — a convenient real lookup for testing, hidden in prod.
const CT_DOT_NUMBER = '3300318'

/**
 * DOT entry step for the one-page estimates flow. Owns only the field + validation;
 * the parent decides what happens on submit (look up the carrier) and on the
 * no-DOT link (switch to the new-authority path).
 */
export function DotEntryPanel({
  isLookingUp,
  errorMessage,
  onSubmit,
  onNewAuthority,
}: {
  isLookingUp: boolean
  /** Server-side error from the DOT lookup (e.g. not found / invalid). */
  errorMessage?: string
  onSubmit: (dot: string) => void
  onNewAuthority: () => void
}) {
  const [dotNumber, setDotNumber] = useState('')
  const [showError, setShowError] = useState(false)

  const trimmed = dotNumber.trim()
  const isValid = DOT_REGEX.test(trimmed)

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (isLookingUp) return
    if (!isValid) {
      setShowError(true)
      return
    }
    onSubmit(trimmed)
  }

  // Local format validation takes precedence; otherwise surface the server error.
  const alert =
    showError && !isValid ? 'That DOT number isn’t valid. Enter your 1–8 digit DOT number.' : errorMessage

  return (
    <div className="flex flex-col gap-3">
      <span className="eyebrow">Insurance Estimates</span>
      <h2 className="text-foreground text-3xl leading-tight font-bold md:text-4xl">
        Let&apos;s start with an auto liability estimate for your fleet
      </h2>

      <form onSubmit={handleSubmit} className="mt-6 flex w-full max-w-md flex-col gap-2">
        {alert && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive mb-1 flex items-start gap-2.5 rounded-lg border p-3.5"
          >
            <TriangleAlert className="mt-0.5 size-5 shrink-0" />
            <span className="text-sm font-semibold">{alert}</span>
          </div>
        )}

        <Input
          id="dot_number"
          name="dot_number"
          size="lg"
          type="text"
          inputMode="numeric"
          pattern="\d*"
          autoComplete="off"
          placeholder="US DOT number"
          maxLength={8}
          value={dotNumber}
          onChange={(e) => {
            setDotNumber(e.target.value)
            setShowError(false)
          }}
          aria-invalid={showError && !isValid ? true : undefined}
          disabled={isLookingUp}
        />
        {/* Dev/staging testing convenience — hidden in production. */}
        {!isProduction && (
          <div className="mb-4">
            <Button
              type="button"
              variant="link"
              onClick={() => setDotNumber(CT_DOT_NUMBER)}
              className="text-ink-500 hover:text-ink h-auto p-0 font-mono text-xs font-normal underline underline-offset-4"
            >
              Use demo DOT
            </Button>
          </div>
        )}

        <Button
          type="submit"
          disabled={isLookingUp}
          loading={isLookingUp ? 'Looking up your DOT…' : undefined}
        >
          Check for savings
          {!isLookingUp && <ArrowRight className="size-4" />}
        </Button>

        <Button type="button" variant="ghost" className="mt-1 w-full" onClick={onNewAuthority}>
          I don&apos;t have a DOT yet.
        </Button>
      </form>
    </div>
  )
}
